import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Save, Users, TrendingUp, Trash2, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// Reordered: Annual first, then Sick, then Others (Compassionate, Maternity, Paternal, Study)
const LEAVE_TYPES = ["Annual Leave", "Sick Leave", "Compassionate Leave", "Maternity Leave", "Paternity Leave", "Study Leave"];

export default function LeaveBalancesPage() {
  const { user } = useAuth();
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [editBalances, setEditBalances] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accrualInfo, setAccrualInfo] = useState(null);
  const [seedStatus, setSeedStatus] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seedBalances, setSeedBalances] = useState({});

  const fetchBalances = () => {
    setLoading(true);
    API.get("/leave/balances/all")
      .then(({ data }) => setBalances(data || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  const fetchAccrualStatus = () => {
    API.get("/leave/accrual-status")
      .then(({ data }) => setAccrualInfo(data))
      .catch(() => {});
  };

  const fetchSeedStatus = () => {
    API.get("/leave/balances/seed-status")
      .then(({ data }) => setSeedStatus(data))
      .catch(() => {});
  };

  useEffect(() => {
    fetchBalances();
    fetchAccrualStatus();
    fetchSeedStatus();
  }, []);

  const openEdit = (bal) => {
    setSelectedEmail(bal.email);
    setEditBalances(bal.balances || {});
    setDialogOpen(true);
  };

  const handleBalanceChange = (leaveType, field, value) => {
    const parsed = parseFloat(value) || 0;
    setEditBalances((prev) => ({
      ...prev,
      [leaveType]: {
        ...(prev[leaveType] || { total: 0, used: 0 }),
        [field]: parsed,
      },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Calculate remaining for each leave type
      const finalBalances = {};
      for (const lt of LEAVE_TYPES) {
        const bal = editBalances[lt] || { total: 0, used: 0 };
        finalBalances[lt] = {
          total: bal.total,
          used: bal.used,
        };
      }
      await API.put("/leave/balances/reconcile", {
        email: selectedEmail,
        balances: finalBalances,
      });
      toast.success("Leave balances updated successfully");
      setDialogOpen(false);
      fetchBalances();
      fetchSeedStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearAll = async () => {
    setSubmitting(true);
    try {
      const { data } = await API.delete("/leave/balances/clear-all");
      toast.success(data.message);
      setClearDialogOpen(false);
      setBalances([]);
      fetchSeedStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const openSeedDialog = () => {
    fetchSeedStatus();
    setSeedDialogOpen(true);
  };

  const handleSeedBalanceChange = (email, value) => {
    const parsed = parseFloat(value) || 0;
    setSeedBalances((prev) => ({
      ...prev,
      [email]: parsed,
    }));
  };

  const handleSeedStartingBalances = async () => {
    if (!seedStatus || seedStatus.employees.length === 0) {
      toast.error("No employees with start dates found to seed");
      return;
    }

    // Build the employees array from the seed status data
    const employees = seedStatus.employees.map((emp) => ({
      email: emp.email,
      carryover_from_previous_year: seedBalances[emp.email] !== undefined ? seedBalances[emp.email] : emp.suggested_carryover,
    }));

    setSubmitting(true);
    try {
      const { data } = await API.post("/leave/balances/seed-starting", { employees });
      toast.success(data.message);
      setSeedDialogOpen(false);
      setSeedBalances({});
      fetchBalances();
      fetchSeedStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccrueMonthly = async (forceSync = false) => {
    const actionLabel = forceSync ? "sync any missed monthly leave accruals" : "accrue this month’s leave";
    if (!window.confirm(`Run a leave accrual ${actionLabel} for ALL active employees?`)) return;
    try {
      const { data } = await API.post("/leave/accrue-monthly", { force_sync: forceSync });
      toast.success(data.message);
      fetchBalances();
      fetchAccrualStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const hasPendingSeed = seedStatus && seedStatus.pending > 0;

  return (
    <div className="space-y-6" data-testid="leave-balances-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          Leave Balances
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={openSeedDialog}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm px-4 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" /> Seed Starting Balances
          </button>
          <button
            onClick={() => setClearDialogOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm px-4 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" /> Clear All Balances
          </button>
          <button
            onClick={() => handleAccrueMonthly(true)}
            disabled={hasPendingSeed}
            className={`rounded-sm px-4 py-2.5 font-medium transition-colors text-sm flex items-center gap-2 ${
              hasPendingSeed
                ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700 text-white"
            }`}
            title={hasPendingSeed ? "Seed starting balances first before syncing accruals" : "Sync missed monthly leave accruals"}
          >
            <RefreshCw className="h-4 w-4" /> Sync Accrual
          </button>
        </div>
      </div>

      {/* Seed Warning Banner */}
      {hasPendingSeed && (
        <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-900">
              Starting Balances Not Yet Seeded
            </p>
            <p className="text-xs text-amber-700 mt-1">
              {seedStatus.pending} employee(s) with start dates have not had their starting balances set yet.
              Please click <strong>"Seed Starting Balances"</strong> to input their accrued leave days before you can accrue monthly.
            </p>
          </div>
        </div>
      )}

      {/* Accrual Info Card */}
      {accrualInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 flex items-start gap-3">
          <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-900">
              Automatic Leave Accrual System
            </p>
            <ul className="text-xs text-blue-700 mt-2 space-y-1 list-disc list-inside">
              <li>Leave accrues automatically at {accrualInfo.monthly_rate} days/month based on employee start date</li>
              <li>Use the Sync Accrual button to catch up any missed monthly accruals</li>
              <li>Maximum annual leave is {accrualInfo.annual_entitlement} days/year</li>
              <li>Use "Reconcile" button to manually set leave balances (e.g., when onboarding from another system)</li>
              <li>Once set, automatic accrual pauses for the current year and resumes next year</li>
              <li>Employees see only their own accrued balance and cannot exceed it</li>
            </ul>
          </div>
        </div>
      )}

      {/* Balances Table */}
      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="px-4 py-3 text-xs font-medium text-slate-500">Employee</TableHead>
              {LEAVE_TYPES.map((lt) => (
                <TableHead key={lt} className="px-3 py-3 text-xs font-medium text-slate-500 text-center">
                  {lt.replace(" Leave", "")}<br />
                  <span className="text-[10px] text-slate-400">Rem/Total</span>
                </TableHead>
              ))}
              <TableHead className="px-4 py-3 text-xs font-medium text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={LEAVE_TYPES.length + 2} className="text-center py-8 text-sm text-slate-500">Loading...</TableCell></TableRow>
            ) : balances.length === 0 ? (
              <TableRow>
                <TableCell colSpan={LEAVE_TYPES.length + 2} className="text-center py-8">
                  <Users className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No leave balances found</p>
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <button onClick={openSeedDialog} className="text-amber-600 text-sm hover:underline">
                      Seed starting balances
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              balances.map((bal) => (
                <TableRow key={bal.email} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <TableCell className="px-4 py-3">
                    <div className="text-sm font-medium text-slate-900">{bal.employee_name}</div>
                    <div className="text-xs text-slate-500">{bal.email}</div>
                  </TableCell>
                  {LEAVE_TYPES.map((lt) => {
                    // Determine the employee's gender from the balance record (if available)
                    const empGender = bal.gender || "";
                    const isInapplicable =
                      (lt === "Maternity Leave" && empGender && empGender !== "female") ||
                      (lt === "Paternity Leave" && empGender && empGender !== "male");

                    const b = isInapplicable
                      ? { remaining: 0, total: 0, used: 0 }
                      : (bal.balances?.[lt] || { remaining: 0, total: 0 });
                    const pct = b.total > 0 ? ((b.used / b.total) * 100) : 0;
                    const isAnnual = lt === "Annual Leave";
                    return (
                      <TableCell key={lt} className={`px-3 py-3 text-center ${isInapplicable ? "opacity-30" : ""}`}>
                        <div className="text-sm font-medium text-slate-900">{b.remaining}</div>
                        <div className="text-xs text-slate-400">/ {b.total}</div>
                        <div className="mt-1 h-1 w-full bg-slate-100 rounded-full overflow-hidden max-w-[40px] mx-auto">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min(pct, 100)}%`,
                              backgroundColor: pct > 80 ? "#EF4444" : pct > 50 ? "#F59E0B" : "#002FA7",
                            }}
                          />
                        </div>
                        {/* Show carryover/accrued breakdown for Annual Leave */}
                        {isAnnual && (
                          <div className="mt-1 text-[9px] text-slate-400 leading-tight">
                            <div>{b.accrued_this_year || 0} accrued</div>
                            <div>{b.carryover_from_previous_year || 0} carryover</div>
                          </div>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell className="px-4 py-3 text-right">
                    <button
                      onClick={() => openEdit(bal)}
                      className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-3 py-1.5 text-xs font-medium transition-colors"
                    >
                      Reconcile
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Reconcile Dialog - Only Annual Leave can be reconciled */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reconcile Annual Leave</DialogTitle>
            <DialogDescription>
              Set the Annual Leave starting balance for <strong>{selectedEmail}</strong> as of the beginning of the year.
              Other leave types (Sick, Compassionate, Study, Maternity/Paternity) are fixed defaults and cannot be manually adjusted.
              After reconciliation, the system will automatically continue accruing based on the employee's start date.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave}>
            <div className="space-y-4 py-4">
              {/* Annual Leave - the only reconcilable type */}
              <div className="bg-blue-50 rounded-sm p-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-blue-600" />
                  <label className="text-sm font-semibold text-blue-900">Annual Leave</label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Starting Balance (Total)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={editBalances["Annual Leave"]?.total || 0}
                      onChange={(e) => handleBalanceChange("Annual Leave", "total", e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-2 py-1.5 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Used</label>
                    <input
                      type="number"
                      step="0.5"
                      value={editBalances["Annual Leave"]?.used || 0}
                      onChange={(e) => handleBalanceChange("Annual Leave", "used", e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-2 py-1.5 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Remaining</label>
                    <div className="bg-white border border-slate-200 rounded-sm px-2 py-1.5 text-sm font-medium text-slate-900 h-[34px] flex items-center">
                      {Math.max(0, (editBalances["Annual Leave"]?.total || 0) - (editBalances["Annual Leave"]?.used || 0))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Other leave types shown as read-only info */}
              <div className="bg-slate-50 rounded-sm p-3 border border-slate-200">
                <p className="text-xs font-semibold text-slate-500 mb-2">Other Leave Types (Fixed Defaults)</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                  {LEAVE_TYPES.filter(lt => lt !== "Annual Leave").map(lt => {
                    const bal = editBalances[lt] || { total: 0, used: 0 };
                    return (
                      <div key={lt} className="flex justify-between items-center bg-white rounded-sm px-2 py-1.5 border border-slate-100">
                        <span className="text-slate-500">{lt.replace(" Leave", "")}</span>
                        <span className="font-medium text-slate-700">{bal.total - bal.used}/{bal.total}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setDialogOpen(false)}
                className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
                {submitting ? "Saving..." : "Save Balances"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Clear All Balances Confirmation Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Clear All Leave Balances
            </DialogTitle>
            <DialogDescription>
              This action will <strong>permanently delete</strong> ALL leave balance records for every employee.
              This is useful if you want to start completely fresh.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="text-sm text-red-800 font-medium">⚠️ Warning</p>
            <ul className="text-xs text-red-700 mt-2 space-y-1 list-disc list-inside">
              <li>All leave balances (Annual, Sick, Compassionate, etc.) will be deleted</li>
              <li>Leave request history will NOT be affected</li>
              <li>You will need to re-seed starting balances before accruing again</li>
              <li>This action cannot be undone</li>
            </ul>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setClearDialogOpen(false)}
              className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
              Cancel
            </button>
            <button type="button" onClick={handleClearAll} disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
              {submitting ? "Clearing..." : "Yes, Clear All Balances"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed Starting Balances Dialog */}
      <Dialog open={seedDialogOpen} onOpenChange={setSeedDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RefreshCw className="h-5 w-5" /> Seed Starting Balances
            </DialogTitle>
            <DialogDescription>
              Set the starting Annual Leave balance for each employee based on their start date.
              This is the first step before you can accrue monthly leave.
            </DialogDescription>
          </DialogHeader>

          {seedStatus && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-600">
                  Total employees with start dates: <strong>{seedStatus.total}</strong>
                </span>
                <span className="text-emerald-600">
                  Reconciled: <strong>{seedStatus.reconciled}</strong>
                </span>
                <span className="text-amber-600">
                  Pending: <strong>{seedStatus.pending}</strong>
                </span>
              </div>

              {/* Employee list for manual seeding */}
              {seedStatus.employees.length > 0 ? (
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="px-3 py-2 text-xs font-medium text-slate-500">Employee</TableHead>
                        <TableHead className="px-3 py-2 text-xs font-medium text-slate-500">Start Date</TableHead>
                        <TableHead className="px-3 py-2 text-xs font-medium text-slate-500">Status</TableHead>
                        <TableHead className="px-3 py-2 text-xs font-medium text-slate-500">Starting Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {seedStatus.employees.map((emp) => (
                        <TableRow key={emp.email} className="border-b border-slate-100">
                          <TableCell className="px-3 py-2">
                            <div className="text-sm font-medium text-slate-900">{emp.name}</div>
                            <div className="text-xs text-slate-500">{emp.email}</div>
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm text-slate-700">{emp.start_date || "—"}</TableCell>
                          <TableCell className="px-3 py-2">
                            {emp.is_reconciled ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                                Reconciled
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                Pending
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              max="21"
                              value={seedBalances[emp.email] !== undefined ? seedBalances[emp.email] : (emp.is_reconciled ? "" : emp.suggested_carryover)}
                              onChange={(e) => handleSeedBalanceChange(emp.email, e.target.value)}
                              disabled={emp.is_reconciled}
                              className={`bg-white border border-slate-200 rounded-sm px-2 py-1 w-20 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm ${
                                emp.is_reconciled ? "opacity-50" : ""
                              }`}
                              placeholder={emp.is_reconciled ? "Done" : "Days"}
                            />
                            {!emp.is_reconciled && (
                              <span className="text-[10px] text-slate-400 ml-1">
                                (suggested: {emp.suggested_carryover})
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-slate-500">
                  No employees with start dates found.
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <button type="button" onClick={() => setSeedDialogOpen(false)}
              className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSeedStartingBalances}
              disabled={submitting || !seedStatus || seedStatus.employees.length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {submitting ? "Seeding..." : "Seed Selected Balances"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
